import {
    useEffect,
    useState,
    useRef
} from "react";
import {
    type KeyboardEventName,
    Keyboard,
    Platform
} from "react-native";
import type {
    TabBarWebScrollConstantType,
    TabBarKeyboardConstantType,
    TabBarScrollStateType,
    TabBarScrollEventType,
    TabBarScrollType
} from "./type";
import {
    TAB_BAR_KEYBOARD_VIEWPORT_RATIO,
    TAB_BAR_INITIAL_SCROLL_STATE
} from "./stylesheet";
import {
    getTabBarScrollState
} from "./util";
import {
    NCoreUIKitTheme
} from "../../core/hooks";

export const useTabBarScroll = (): TabBarScrollType => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        isHidden,
        setIsHidden
    ] = useState<boolean>(false);

    const stateRef = useRef<TabBarScrollStateType>(TAB_BAR_INITIAL_SCROLL_STATE);

    const onScroll = ({
        nativeEvent
    }: TabBarScrollEventType) => {
        stateRef.current = getTabBarScrollState({
            layoutHeight: nativeEvent.layoutMeasurement.height,
            contentHeight: nativeEvent.contentSize.height,
            offset: nativeEvent.contentOffset.y,
            previousState: stateRef.current,
            threshold: spaces.spacingSm
        });

        setIsHidden(stateRef.current.isHidden);
    };

    return {
        isHidden,
        onScroll
    };
};

export const useTabBarKeyboard = ({
    isEnabled
}: TabBarKeyboardConstantType): boolean => {
    const [
        isKeyboardOpen,
        setIsKeyboardOpen
    ] = useState<boolean>(false);

    useEffect(() => {
        if(!isEnabled) {
            setIsKeyboardOpen(false);

            return;
        }

        if(Platform.OS === "web") {
            const viewport = window.visualViewport;

            if(!viewport) {
                return;
            }

            const onResize = () => {
                setIsKeyboardOpen(viewport.scale === 1 && viewport.height < window.innerHeight * TAB_BAR_KEYBOARD_VIEWPORT_RATIO);
            };

            viewport.addEventListener("resize", onResize);

            return () => {
                viewport.removeEventListener("resize", onResize);
            };
        }

        const showEvent: KeyboardEventName = Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow";

        const hideEvent: KeyboardEventName = Platform.OS === "ios" ? "keyboardWillHide" : "keyboardDidHide";

        const showSubscription = Keyboard.addListener(showEvent, () => {
            setIsKeyboardOpen(true);
        });

        const hideSubscription = Keyboard.addListener(hideEvent, () => {
            setIsKeyboardOpen(false);
        });

        return () => {
            showSubscription.remove();

            hideSubscription.remove();
        };
    }, [
        isEnabled
    ]);

    return isKeyboardOpen;
};

export const useTabBarWebScroll = ({
    isEnabled
}: TabBarWebScrollConstantType): boolean => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        isHidden,
        setIsHidden
    ] = useState<boolean>(false);

    useEffect(() => {
        if(Platform.OS !== "web" || !isEnabled) {
            setIsHidden(false);

            return;
        }

        const states = new WeakMap<EventTarget, TabBarScrollStateType>();

        const onScroll = (event: Event) => {
            const target = event.target;

            if(!target) {
                return;
            }

            const element = target instanceof Element ? target : document.scrollingElement;

            if(!element) {
                return;
            }

            const nextState = getTabBarScrollState({
                previousState: states.get(target) ?? {
                    anchorOffset: element.scrollTop,
                    isHidden: false
                },
                contentHeight: element.scrollHeight,
                layoutHeight: element.clientHeight,
                threshold: spaces.spacingSm,
                offset: element.scrollTop
            });

            states.set(target, nextState);

            setIsHidden(nextState.isHidden);
        };

        document.addEventListener("scroll", onScroll, true);

        return () => {
            document.removeEventListener("scroll", onScroll, true);

            setIsHidden(false);
        };
    }, [
        spaces.spacingSm,
        isEnabled
    ]);

    return isHidden;
};
